// src/Redux/slices/cycleBotSlice.js

import { createSlice } from "@reduxjs/toolkit";
import {
  fetchCycleBotHealth,
  initCycleChat,
  sendCycleMessage,
  logCycleEntry,
  fetchJournalSummary,
  fetchJournalAnalysis,
  fetchJournalEntries,
  fetchFlowsList,
  fetchFlowDetail,
  beginCycleFlow,
  answerCycleFlow,
  fetchPremenstrualReport,
  resetCycleAtJ1,
  fetchJournalHistory,
  fetchCycleFlowReport,
  deleteCycleFlowSession,
} from "../thunks/cycleBotThunks";

const initialState = {
  health:          { data: null,  loading: false, error: null },
  chatSession:     { data: null,  loading: false, error: null },
  messages:        [],
  isTyping:        false,
  journalSummary:  { data: null,  loading: false, error: null },
  journalAnalysis: { data: null,  loading: false, error: null },
  journalEntries:  { data: [],    loading: false, error: null },
  logResult:       { data: null,  loading: false, error: null },
  flowsList:       { data: [],    loading: false, error: null },
  flowDetail:      { data: null,  loading: false, error: null },
  flowSession:     { data: null,  loading: false, error: null },
  flowReport:      { data: null,  loading: false, error: null },
  isGeneratingReport: false,
  premenstrualReport:  { data: null, loading: false, error: null },
  cycleReset:          { data: null, loading: false, error: null },
  journalHistory:      { data: null, loading: false, error: null },
};

/* ── normalize : unwrap { data: ... } si présent ── */
const unwrap = (payload) => payload?.data ?? payload;

const cycleBotSlice = createSlice({
  name: "cycleBot",
  initialState,

  reducers: {
    addUserMessage(state, { payload }) {
      state.messages.push({
        role:      "user",
        content:   payload,
        timestamp: new Date().toISOString(),
      });
      state.isTyping = true;
    },
    resetChat(state) {
      state.messages    = [];
      state.chatSession = { data: null, loading: false, error: null };
      state.isTyping    = false;
    },
    resetFlowSession(state) {
      state.flowSession = { data: null, loading: false, error: null };
      state.flowReport  = { data: null, loading: false, error: null };
      state.isGeneratingReport = false;
    },
    clearError(state, { payload }) {
      if (state[payload]) state[payload].error = null;
    },
  },

  extraReducers: (builder) => {

    /* ════ HEALTH ════ */
    builder
      .addCase(fetchCycleBotHealth.pending,   (s) => { s.health.loading = true; s.health.error = null; })
      .addCase(fetchCycleBotHealth.fulfilled, (s, { payload }) => { s.health = { data: unwrap(payload), loading: false, error: null }; })
      .addCase(fetchCycleBotHealth.rejected,  (s, { payload }) => { s.health = { data: null, loading: false, error: payload }; });

    /* ════ CHAT : initCycleChat ════ */
    builder
      .addCase(initCycleChat.pending, (s) => {
        s.chatSession.loading = true;
        s.chatSession.error   = null;
        s.isTyping            = true;
      })
      .addCase(initCycleChat.fulfilled, (s, { payload }) => {
        const d = unwrap(payload);
        s.chatSession = { data: d, loading: false, error: null };
        s.isTyping    = false;
        // Premier message bot
        s.messages = [{
          role:      "bot",
          content:   d?.response || d?.message || "Bonjour !",
          timestamp: new Date().toISOString(),
          choices:   d?.choices || [],
          alerts:    d?.alerts  || [],
        }];
      })
      .addCase(initCycleChat.rejected, (s, { payload }) => {
        s.chatSession = { data: null, loading: false, error: payload };
        s.isTyping    = false;
      });

    /* ════ CHAT : sendCycleMessage ════ */
    builder
      .addCase(sendCycleMessage.pending,   (s) => { s.isTyping = true; })
      .addCase(sendCycleMessage.fulfilled, (s, { payload }) => {
        const d = unwrap(payload);
        s.isTyping = false;
        s.messages.push({
          role:      "bot",
          content:   d?.response || d?.message || "...",
          timestamp: new Date().toISOString(),
          choices:   d?.choices || [],
          alerts:    d?.alerts  || [],
        });
      })
      .addCase(sendCycleMessage.rejected, (s, { payload }) => {
        s.isTyping = false;
        s.messages.push({ role: "bot", content: `❌ ${payload || "Erreur"}`, timestamp: new Date().toISOString(), choices: [] });
      });

    /* ════ JOURNAL : logCycleEntry ════ */
    builder
      .addCase(logCycleEntry.pending,   (s) => { s.logResult.loading = true; s.logResult.error = null; })
      .addCase(logCycleEntry.fulfilled, (s, { payload }) => { s.logResult = { data: unwrap(payload), loading: false, error: null }; s.journalEntries.needsRefresh = true; })
      .addCase(logCycleEntry.rejected,  (s, { payload }) => { s.logResult = { data: null, loading: false, error: payload }; });

    /* ════ JOURNAL : fetchJournalSummary ════ */
    builder
      .addCase(fetchJournalSummary.pending,   (s) => { s.journalSummary.loading = true; s.journalSummary.error = null; })
      .addCase(fetchJournalSummary.fulfilled, (s, { payload }) => { s.journalSummary = { data: unwrap(payload), loading: false, error: null }; })
      .addCase(fetchJournalSummary.rejected,  (s, { payload }) => { s.journalSummary = { data: null, loading: false, error: payload }; });

    /* ════ JOURNAL : fetchJournalAnalysis ════ */
    builder
      .addCase(fetchJournalAnalysis.pending,   (s) => { s.journalAnalysis.loading = true; s.journalAnalysis.error = null; })
      .addCase(fetchJournalAnalysis.fulfilled, (s, { payload }) => { s.journalAnalysis = { data: unwrap(payload), loading: false, error: null }; })
      .addCase(fetchJournalAnalysis.rejected,  (s, { payload }) => { s.journalAnalysis = { data: null, loading: false, error: payload }; });

    /* ════ JOURNAL : fetchJournalEntries ════ */
    builder
      .addCase(fetchJournalEntries.pending,   (s) => { s.journalEntries.loading = true; s.journalEntries.error = null; })
      .addCase(fetchJournalEntries.fulfilled, (s, { payload }) => {
        const d = unwrap(payload);
        // Le back peut retourner { entries: [] } ou directement []
        s.journalEntries = { data: d?.entries ?? d ?? [], loading: false, error: null };
      })
      .addCase(fetchJournalEntries.rejected,  (s, { payload }) => { s.journalEntries = { data: [], loading: false, error: payload }; });

    /* ════ FLOWS : fetchFlowsList ════ */
    builder
      .addCase(fetchFlowsList.pending,   (s) => { s.flowsList.loading = true; s.flowsList.error = null; })
      .addCase(fetchFlowsList.fulfilled, (s, { payload }) => {
        const d = unwrap(payload);
        // Back peut retourner { flows: [] } ou []
        s.flowsList = { data: d?.flows ?? d ?? [], loading: false, error: null };
      })
      .addCase(fetchFlowsList.rejected,  (s, { payload }) => { s.flowsList = { data: [], loading: false, error: payload }; });

    /* ════ FLOWS : fetchFlowDetail ════ */
    builder
      .addCase(fetchFlowDetail.pending,   (s) => { s.flowDetail.loading = true; s.flowDetail.error = null; })
      .addCase(fetchFlowDetail.fulfilled, (s, { payload }) => { s.flowDetail = { data: unwrap(payload), loading: false, error: null }; })
      .addCase(fetchFlowDetail.rejected,  (s, { payload }) => { s.flowDetail = { data: null, loading: false, error: payload }; });

    /* ════ FLOWS : beginCycleFlow ════ */
    builder
      .addCase(beginCycleFlow.pending, (s) => {
        s.flowSession.loading = true;
        s.flowSession.error   = null;
        s.flowReport          = { data: null, loading: false, error: null };
      })
      .addCase(beginCycleFlow.fulfilled, (s, { payload }) => {
        const d = unwrap(payload);
        // Normalise : stocke flow_id et session_id à la racine pour answerFlow
        s.flowSession = {
          data: {
            ...d,
            _flow_id:    d?.flow?.id ?? d?.flow_id ?? d?.flow?.flow_id,
            _session_id: d?.session_id ?? d?.id,
          },
          loading: false,
          error: null,
        };
      })
      .addCase(beginCycleFlow.rejected,  (s, { payload }) => { s.flowSession = { data: null, loading: false, error: payload }; });

    /* ════ FLOWS : answerCycleFlow ════ */
    builder
      .addCase(answerCycleFlow.pending, (s) => { s.flowSession.loading = true; s.flowSession.error = null; s.isGeneratingReport = true; })
      .addCase(answerCycleFlow.fulfilled, (s, { payload }) => {
        const d = unwrap(payload);
        if (d?.completed) {
          s.isGeneratingReport = false;
          s.flowReport  = { data: d.report ?? d, loading: false, error: null };
          s.flowSession = { data: null, loading: false, error: null };
        } else {
          s.isGeneratingReport = false;
          // Préserve _flow_id et _session_id entre les questions
          const prev = s.flowSession.data;
          s.flowSession = {
            data: {
              ...d,
              _flow_id:    d?.flow?.id ?? d?.flow_id ?? prev?._flow_id,
              _session_id: d?.session_id ?? prev?._session_id,
            },
            loading: false,
            error: null,
          };
        }
      })
      .addCase(answerCycleFlow.rejected, (s, { payload }) => {
        s.isGeneratingReport = false;
        s.flowSession = { ...s.flowSession, loading: false, error: payload };
      });

    /* ════ FLOWS : fetchCycleFlowReport ════ */
    builder
      .addCase(fetchCycleFlowReport.pending,   (s) => { s.flowReport.loading = true; s.flowReport.error = null; })
      .addCase(fetchCycleFlowReport.fulfilled, (s, { payload }) => { s.flowReport = { data: unwrap(payload), loading: false, error: null }; })
      .addCase(fetchCycleFlowReport.rejected,  (s, { payload }) => { s.flowReport = { data: null, loading: false, error: payload }; });

    /* ════ FLOWS : deleteCycleFlowSession ════ */
    builder
      .addCase(deleteCycleFlowSession.fulfilled, (s) => {
        s.flowSession = { data: null, loading: false, error: null };
        s.flowReport  = { data: null, loading: false, error: null };
      });

    /* ════ RAPPORT PRÉ-MENSTRUEL ════ */
    builder
      .addCase(fetchPremenstrualReport.pending,   (s) => {
        s.premenstrualReport = { data: null, loading: true, error: null };
      })
      .addCase(fetchPremenstrualReport.fulfilled, (s, { payload }) => {
        s.premenstrualReport = { data: unwrap(payload), loading: false, error: null };
      })
      .addCase(fetchPremenstrualReport.rejected,  (s, { payload }) => {
        s.premenstrualReport = { data: null, loading: false, error: payload };
      });

    /* ════ RESET CYCLE ════ */
    builder
      .addCase(resetCycleAtJ1.pending,   (s) => { s.cycleReset = { data: null, loading: true, error: null }; })
      .addCase(resetCycleAtJ1.fulfilled, (s, { payload }) => {
        s.cycleReset         = { data: payload, loading: false, error: null };
        s.journalEntries     = { data: [], loading: false, error: null };
        s.journalSummary     = { data: null, loading: false, error: null };
        s.premenstrualReport = { data: null, loading: false, error: null };
      })
      .addCase(resetCycleAtJ1.rejected,  (s, { payload }) => { s.cycleReset = { data: null, loading: false, error: payload }; });

    /* ════ HISTORIQUE ════ */
    builder
      .addCase(fetchJournalHistory.pending,   (s) => { s.journalHistory = { data: null, loading: true, error: null }; })
      .addCase(fetchJournalHistory.fulfilled, (s, { payload }) => { s.journalHistory = { data: payload, loading: false, error: null }; })
      .addCase(fetchJournalHistory.rejected,  (s, { payload }) => { s.journalHistory = { data: null, loading: false, error: payload }; });
  },
});

export const { addUserMessage, resetChat, resetFlowSession, clearError } = cycleBotSlice.actions;
export default cycleBotSlice.reducer;